import React, { useMemo, useState } from 'react';
import { PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { CANALES, cuotasIguales, eur, hoyISO, problemasDelPlan, sumaCuotas, sumarDias } from './paymentPlanUtils';

/**
 * El alta de un plan de pagos (docs/planes-de-pago-fase-1-plan.md, §2): las
 * cuotas (fecha e importe), el canal por el que se sigue y unas notas.
 *
 * El atajo «N cuotas iguales cada X días» rellena la tabla; luego se puede
 * retocar cada fila. Lo que ya se sabe que el servidor va a rechazar se dice
 * aquí y no deja enviar; el resto de errores se enseñan tal como vienen.
 */
const PaymentPlanForm = ({ debt, submitting = false, error = null, submitLabel = 'Crear plan', onSubmit, onCancel }) => {
  const hoy = hoyISO();
  const [atajo, setAtajo] = useState({ count: 3, everyDays: 30, from: sumarDias(hoy, 7), total: debt ?? '' });
  const [cuotas, setCuotas] = useState(() =>
    cuotasIguales({ total: debt || 0, count: 3, everyDays: 30, from: sumarDias(hoy, 7) })
  );
  const [channel, setChannel] = useState('whatsapp');
  const [notes, setNotes] = useState('');

  const problemas = useMemo(() => problemasDelPlan(cuotas, debt), [cuotas, debt]);
  const total = sumaCuotas(cuotas);

  const setCuota = (i, campo) => e => setCuotas(cs => cs.map((c, j) => (j === i ? { ...c, [campo]: e.target.value } : c)));
  const quitar = i => setCuotas(cs => cs.filter((_, j) => j !== i));
  const anadir = () =>
    setCuotas(cs => [...cs, { due_date: cs.length ? sumarDias(cs[cs.length - 1].due_date, 30) : sumarDias(hoy, 7), amount: '' }]);
  const aplicarAtajo = () => setCuotas(cuotasIguales(atajo));

  const enviar = e => {
    e.preventDefault();
    if (problemas.length) return;
    onSubmit({
      installments: cuotas.map(c => ({ due_date: c.due_date, amount: Number(c.amount) })),
      channel,
      ...(notes.trim() ? { notes: notes.trim() } : {}),
    });
  };

  const campo = 'rounded-lg border-slate-300 text-sm';

  return (
    <form onSubmit={enviar} className='space-y-4' aria-label='Plan de pagos'>
      <div className='flex flex-wrap items-end gap-2 bg-slate-50 border border-slate-200 rounded-lg p-3 text-sm text-slate-600'>
        <label className='flex flex-col gap-1'>
          Cuotas
          <input
            type='number'
            min='1'
            max='60'
            aria-label='Número de cuotas'
            value={atajo.count}
            onChange={e => setAtajo(a => ({ ...a, count: e.target.value }))}
            className={`${campo} w-20`}
          />
        </label>
        <label className='flex flex-col gap-1'>
          Cada (días)
          <input
            type='number'
            min='1'
            aria-label='Días entre cuotas'
            value={atajo.everyDays}
            onChange={e => setAtajo(a => ({ ...a, everyDays: e.target.value }))}
            className={`${campo} w-24`}
          />
        </label>
        <label className='flex flex-col gap-1'>
          Primera
          <input
            type='date'
            aria-label='Fecha de la primera cuota'
            min={hoy}
            value={atajo.from}
            onChange={e => setAtajo(a => ({ ...a, from: e.target.value }))}
            className={campo}
          />
        </label>
        <label className='flex flex-col gap-1'>
          Total (€)
          <input
            type='number'
            step='0.01'
            min='0'
            aria-label='Importe total'
            value={atajo.total}
            onChange={e => setAtajo(a => ({ ...a, total: e.target.value }))}
            className={`${campo} w-32`}
          />
        </label>
        <button type='button' onClick={aplicarAtajo} className='rounded-lg border border-slate-300 bg-white px-3 py-2 hover:bg-slate-100'>
          Repartir en cuotas iguales
        </button>
      </div>

      <table className='min-w-full text-sm'>
        <thead className='text-xs text-slate-500'>
          <tr>
            <th className='text-left font-medium py-1 w-10'>#</th>
            <th className='text-left font-medium py-1'>Vence</th>
            <th className='text-left font-medium py-1'>Importe (€)</th>
            <th className='w-10' />
          </tr>
        </thead>
        <tbody>
          {cuotas.map((c, i) => (
            <tr key={i}>
              <td className='py-1 text-slate-500'>{i + 1}</td>
              <td className='py-1 pr-2'>
                <input type='date' aria-label={`Vencimiento de la cuota ${i + 1}`} value={c.due_date} onChange={setCuota(i, 'due_date')} className={campo} />
              </td>
              <td className='py-1 pr-2'>
                <input
                  type='number'
                  step='0.01'
                  min='0'
                  aria-label={`Importe de la cuota ${i + 1}`}
                  value={c.amount}
                  onChange={setCuota(i, 'amount')}
                  className={`${campo} w-32`}
                />
              </td>
              <td className='py-1'>
                <button type='button' onClick={() => quitar(i)} aria-label={`Quitar la cuota ${i + 1}`} className='text-slate-400 hover:text-rose-600'>
                  <TrashIcon className='w-4 h-4' />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className='flex flex-wrap items-center justify-between gap-2 text-sm'>
        <button type='button' onClick={anadir} className='inline-flex items-center gap-1 text-indigo-600 hover:underline'>
          <PlusIcon className='w-4 h-4' /> Añadir cuota
        </button>
        <span className='text-slate-600'>
          Suman <strong>{eur(total)}</strong> · deuda viva {eur(debt)}
        </span>
      </div>

      <div className='flex flex-wrap gap-4'>
        <label className='flex flex-col gap-1 text-sm text-slate-600'>
          Seguimiento por
          <select aria-label='Canal del seguimiento' value={channel} onChange={e => setChannel(e.target.value)} className={campo}>
            {CANALES.map(c => (
              <option key={c.value} value={c.value}>
                {c.label}
              </option>
            ))}
          </select>
        </label>
        <label className='flex flex-col gap-1 text-sm text-slate-600 flex-1 min-w-[16rem]'>
          Notas
          <textarea aria-label='Notas del plan' value={notes} onChange={e => setNotes(e.target.value)} rows={2} className={campo} />
        </label>
      </div>
      <p className='text-xs text-slate-500'>
        El día de cada vencimiento se crea una tarea de seguimiento por este canal para pedir el justificante de pago.
      </p>

      {problemas.length > 0 && (
        <ul className='text-sm text-amber-700 list-disc pl-5' aria-label='Problemas del plan'>
          {problemas.map(p => (
            <li key={p}>{p}</li>
          ))}
        </ul>
      )}
      {error && (
        <p role='alert' className='text-sm text-rose-700'>
          {error}
        </p>
      )}

      <div className='flex gap-2'>
        <button
          type='submit'
          disabled={submitting || problemas.length > 0}
          className='rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50'
        >
          {submitting ? 'Guardando…' : submitLabel}
        </button>
        {onCancel && (
          <button type='button' onClick={onCancel} className='rounded-lg px-4 py-2 text-sm text-slate-600 hover:bg-slate-100'>
            Volver
          </button>
        )}
      </div>
    </form>
  );
};

export default PaymentPlanForm;
